import React from 'react';
import { CONFIG_STEP, MANDATE_APPLICANT_LABELS } from '../../DealDrawerConstants';
import { BusinessDetailsSection } from './BusinessDetailsSection';
import { ApplicantPicker } from './ApplicantPicker';
import { ApplicantFieldsBlock } from './ApplicantFieldsBlock';
import { CoApplicantSection } from './CoApplicantSection';
import { OwnedPropertySection } from './OwnedPropertySection';
import { collectEnteredOwnerships, isOwnedPropertyBlockVisible } from './ownedPropertyTypes';
import { ApplicantDirectorLink, CoApplicantRow, ExistingDirectorOption } from './coApplicantTypes';
import { FormSectionPassThroughProps } from './formSectionPassThroughTypes';
import styles from '../../ManageDealDrawer.module.scss';

interface ApplicationFormStepProps {
    currentConfig: any;
    formData: Record<string, any>;
    handleChange: (key: string, value: any, importanceKey?: string | null) => void;
    assesseeOrgId?: string;
    dealDrawerState: any;
    existingDirectors: ExistingDirectorOption[];
    applicantPickerSelectedIds: (string | null)[];
    handleApplicantPickerChange: (nextSelectedIds: (string | null)[]) => void;
    handleAddApplicant: () => void;
    applicantPrimaryTypeError: string;
    duplicateApplicantError: string;
    primarySlotActive: boolean;
    primaryApplicantLink: ApplicantDirectorLink;
    clearPrimaryApplicant: () => void;
    coApplicants: CoApplicantRow[];
    removeCoApplicantRow: (id: string) => void;
    updateCoApplicantField: (id: string, fieldKey: string, value: any, importanceKey?: string | null) => void;
    formSectionPassThroughProps: FormSectionPassThroughProps;
}

// Application Form's Business Details + Select Applicants + Primary/Co-Applicant cards block -
// rendered only for the PRIMARY_APPLICANT step (see ManageDealDrawer's currentConfig?.step check).
export const ApplicationFormStep: React.FC<ApplicationFormStepProps> = ({
    currentConfig,
    formData,
    handleChange,
    assesseeOrgId,
    dealDrawerState,
    existingDirectors,
    applicantPickerSelectedIds,
    handleApplicantPickerChange,
    handleAddApplicant,
    applicantPrimaryTypeError,
    duplicateApplicantError,
    primarySlotActive,
    primaryApplicantLink,
    clearPrimaryApplicant,
    coApplicants,
    removeCoApplicantRow,
    updateCoApplicantField,
    formSectionPassThroughProps,
}) => {
    const primaryApplicantSection = currentConfig.config?.find((section: any) => section.sectionKey === CONFIG_STEP.primaryApplicant);
    const isReadonly = !!dealDrawerState?.readonly;
    const showOwnedProperty = isOwnedPropertyBlockVisible(collectEnteredOwnerships(formData, primarySlotActive, coApplicants));

    return (
        <>
            <BusinessDetailsSection formData={formData} handleChange={handleChange} assesseeOrgId={assesseeOrgId} readonly={isReadonly} />

            <div className={styles.Section}>
                <div className={styles.SectionHeading}>{MANDATE_APPLICANT_LABELS.sectionHeading}</div>

                <ApplicantPicker
                    existingDirectors={existingDirectors}
                    selectedManagementProfileIds={applicantPickerSelectedIds}
                    onSelectionChange={handleApplicantPickerChange}
                    onAdd={handleAddApplicant}
                    disabled={isReadonly}
                />

                {applicantPrimaryTypeError && (
                    <div className={styles.ApplicantPrimaryTypeError} role="alert">
                        {applicantPrimaryTypeError}
                    </div>
                )}

                {duplicateApplicantError && (
                    <div className={styles.ApplicantPrimaryTypeError} role="alert">
                        {duplicateApplicantError}
                    </div>
                )}

                {primarySlotActive && (
                    <ApplicantFieldsBlock
                        sectionKey={CONFIG_STEP.primaryApplicant}
                        applicantConfig={primaryApplicantSection?.config}
                        formData={formData}
                        handleChange={handleChange}
                        directorId={primaryApplicantLink?.directorId}
                        managementProfileId={primaryApplicantLink?.managementProfileId}
                        linkSource={primaryApplicantLink?.linkSource}
                        onRemove={clearPrimaryApplicant}
                        {...formSectionPassThroughProps}
                    />
                )}

                <CoApplicantSection
                    primaryApplicantSection={primaryApplicantSection}
                    coApplicants={coApplicants}
                    onRemove={removeCoApplicantRow}
                    onFieldChange={updateCoApplicantField}
                    {...formSectionPassThroughProps}
                />
            </div>

            {showOwnedProperty && <OwnedPropertySection formData={formData} handleChange={handleChange} readonly={isReadonly} />}
        </>
    );
};
